import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Moon,         // Success animation crescent moon
  Globe, 
  Lock, 
  Rocket,       // Submit button & step indicator
  Shield, 
  Bot, 
  Earth, 
  DollarSign, 
  Target, 
  Trophy, 
  BarChart, 
  Star,
  UserPlus,
  Loader,
  X,
  User,
  Zap,
  Award,
  AlertTriangle,
  Brain,
  Cpu,          // Technology
  Landmark,     // Politics
  TrendingUp,   // Economy
  Leaf,         // Environment
  Heart,        // Healthcare
  Users,        // Society
  Film,         // Entertainment
  Flame,        // Trendphile
  MoreHorizontal // Other
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ShareSuccessModal from '../components/ShareSuccessModal';
 // Add this line
import './Submit.css';


function Submit() {
    const [text, setText] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [visibility, setVisibility] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showLoginBanner, setShowLoginBanner] = useState(true);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showShareModal, setShowShareModal] = useState(false);
    const [submittedPrediction, setSubmittedPrediction] = useState(null);
    const [stats, setStats] = useState({
        predictionsThisWeek: 2847,
        newUsersThisWeek: 93
    });
    
    // ✨ NEW: Form Progress State
    const [formProgress, setFormProgress] = useState({
        hasText: false,
        hasCategory: false,
        isReadyToSubmit: false
    });
    
    
    
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth(); // Use auth context
    const token = localStorage.getItem('token') || sessionStorage.getItem('token'); // Keep token for API calls
    const observerRef = useRef();
    const textareaRef = useRef();


    // ✅ UPDATED: Categories arranged for 2x5 grid with icons
    const availableCategories = [
        'Technology', 'Politics', 'Economy', 'Environment', 'Healthcare',
        'Society', 'Entertainment', 'Sports', 'Trendphile', 'Other'
    ];

    // 🎨 Category icon mapping
    const categoryIcons = {
        'Technology': Cpu,
        'Politics': Landmark,
        'Economy': TrendingUp,
        'Environment': Leaf,
        'Healthcare': Heart,
        'Society': Users,
        'Entertainment': Film,
        'Sports': Trophy,
        'Trendphile': Flame,
        'Other': MoreHorizontal
    };

    // 🏷️ Category display names (shortened for better fit)
    const categoryLabels = {
        'Technology': 'Tech',
        'Politics': 'Politics',
        'Economy': 'Economy',
        'Environment': 'Environment',
        'Healthcare': 'Health',
        'Society': 'Society',
        'Entertainment': 'Media',
        'Sports': 'Sports',
        'Trendphile': 'Trendphile',
        'Other': 'Other'
    };

    // Your existing categoryKeywords - keeping unchanged
    const categoryKeywords = useMemo(() => ({
        Technology: {
          primary: [
            'AI', 'machine learning', 'blockchain', 'cryptocurrency', 'bitcoin', 
            'software', 'app', 'mobile', 'web', 'tech', 'digital', 'data', 
            'programming', 'coding', 'developer', 'startup', 'innovation',
            'cloud', 'cybersecurity', 'automation', 'IoT', 'VR',
            'هوش مصنوعی', 'فناوری', 'نرم‌افزار', 'برنامه‌نویسی', 'داده', 
            'الگوریتم', 'پایگاه داده', 'رمزارز', 'بلاک‌چین', 'ابر', 'امنیت سایبری'
          ],
          secondary: [
            'artificial intelligence', 'deep learning', 'neural network', 
            'algorithm', 'database', 'API', 'framework', 'platform',
            'infrastructure', 'analytics', 'computing', 'hardware'
          ]
        },
        
        Politics: {
          primary: [
            'politics', 'government', 'election', 'vote', 'democracy', 'policy',
            'law', 'legislation', 'congress', 'parliament', 'president',
            'minister', 'politician', 'campaign', 'debate', 'reform',
            'constitution', 'rights', 'freedom', 'justice',
            'سیاست', 'دولت', 'انتخابات', 'رأی', 'دموکراسی', 'سیاست‌گذاری',
            'قانون', 'قانون‌گذاری', 'مجلس', 'رئیس‌جمهور', 'وزیر',
            'سیاستمدار', 'کمپین', 'مناظره', 'اصلاحات', 'قانون اساسی',
            'حقوق', 'آزادی', 'عدالت'
          ],
          secondary: [
            'legislature', 'judiciary', 'executive', 'bipartisan',
            'referendum', 'constituency', 'mandate', 'diplomacy',
            'sovereignty', 'governance', 'bureaucracy', 'civil service'
          ]
        },
      
        Economy: {
          primary: [
            'economy', 'economic', 'finance', 'financial', 'money', 'investment',
            'market', 'stock', 'trade', 'business', 'bank', 'banking',
            'inflation', 'recession', 'GDP', 'growth', 'unemployment',
            'tax', 'budget', 'debt', 'revenue', 'profit', 'loss',
            'اقتصاد', 'اقتصادی', 'مالی', 'پول', 'سرمایه‌گذاری',
            'بازار', 'سهام', 'تجارت', 'بانک', 'بانکداری',
            'تورم', 'رکود', 'رشد', 'بیکاری', 'مالیات', 'بودجه', 'بدهی'
          ],
          secondary: [
            'macroeconomics', 'microeconomics', 'monetary policy', 'fiscal policy',
            'supply and demand', 'commodity', 'currency', 'exchange rate',
            'interest rate', 'central bank', 'federal reserve'
          ]
        },
      
        Environment: {
          primary: [
            'environment', 'climate', 'global warming', 'pollution', 'recycling',
            'sustainability', 'renewable', 'solar', 'wind', 'energy',
            'carbon', 'emissions', 'green', 'eco', 'nature', 'wildlife',
            'conservation', 'ecosystem', 'biodiversity', 'deforestation',
            'محیط‌زیست', 'آب‌وهوا', 'گرمایش زمین', 'آلودگی', 'بازیافت',
            'پایداری', 'تجدیدپذیر', 'خورشیدی', 'بادی', 'انرژی',
            'کربن', 'انتشار', 'سبز', 'طبیعت', 'حیات‌وحش', 'حفاظت'
          ],
          secondary: [
            'greenhouse gas', 'ozone layer', 'fossil fuels', 'hydroelectric',
            'geothermal', 'photovoltaic', 'wind turbine', 'carbon footprint',
            'sustainable development', 'environmental impact'
          ]
        },
      
        Healthcare: {
          primary: [
            'health', 'healthcare', 'medical', 'medicine', 'doctor', 'hospital',
            'treatment', 'disease', 'wellness', 'fitness', 'nutrition', 'diet',
            'mental health', 'therapy', 'patient', 'diagnosis', 'vaccine',
            'pandemic', 'virus', 'surgery',
            'سلامت', 'بهداشت', 'پزشکی', 'داروخانه', 'بیمارستان', 'درمان',
            'بیماری', 'تندرستی', 'تغذیه', 'رژیم', 'سلامت روان',
            'درمانگر', 'بیمار', 'تشخیص', 'واکسن', 'ویروس', 'جراحی'
          ],
          secondary: [
            'pharmaceutical', 'clinical', 'therapeutic', 'pathology',
            'epidemiology', 'immunology', 'cardiology', 'neurology',
            'oncology', 'psychiatry', 'rehabilitation', 'telemedicine'
          ]
        },
      
        Society: {
          primary: [
            'society', 'social', 'community', 'culture', 'religion', 'family',
            'education', 'school', 'university', 'human rights', 'equality',
            'diversity', 'immigration', 'population', 'demographics',
            'poverty', 'welfare', 'charity', 'volunteer', 'activism',
            'جامعه', 'اجتماعی', 'جامعه', 'فرهنگ', 'مذهب', 'خانواده',
            'آموزش', 'مدرسه', 'دانشگاه', 'حقوق بشر', 'برابری',
            'تنوع', 'مهاجرت', 'جمعیت', 'فقر', 'رفاه', 'خیریه'
          ],
          secondary: [
            'sociology', 'anthropology', 'social justice', 'civil rights',
            'gender equality', 'ethnic minority', 'social mobility',
            'urbanization', 'rural development', 'social welfare'
          ]
        },
      
        Entertainment: {
          primary: [
            'entertainment', 'movie', 'film', 'music', 'game', 'gaming', 'video',
            'TV', 'television', 'show', 'celebrity', 'actor', 'artist',
            'concert', 'festival', 'comedy', 'drama', 'action', 'horror',
            'سرگرمی', 'فیلم', 'موسیقی', 'بازی', 'ویدیو', 'تلویزیون',
            'نمایش', 'سلبریتی', 'بازیگر', 'هنرمند', 'کنسرت',
            'جشنواره', 'کمدی', 'درام', 'اکشن', 'ترسناک'
          ],
          secondary: [
            'cinematography', 'screenplay', 'soundtrack', 'genre',
            'blockbuster', 'indie', 'streaming', 'broadcast', 'production',
            'animation', 'visual effects', 'box office'
          ]
        },
      
        Sports: {
          primary: [
            'sports', 'football', 'soccer', 'basketball', 'baseball', 'tennis',
            'golf', 'swimming', 'running', 'fitness', 'gym', 'athlete',
            'team', 'match', 'game', 'competition', 'championship', 'league',
            'olympics', 'training', 'coach',
            'ورزش', 'فوتبال', 'بسکتبال', 'تنیس', 'گلف', 'شنا', 'دویدن',
            'تناسب اندام', 'باشگاه', 'ورزشکار', 'تیم', 'مسابقه', 'بازی',
            'مسابقات', 'قهرمانی', 'لیگ', 'المپیک', 'تمرین', 'مربی'
          ],
          secondary: [
            'tournament', 'playoff', 'semi-final', 'qualifier', 'draft',
            'rookie', 'veteran', 'statistics', 'performance', 'endurance',
            'strength', 'agility', 'conditioning'
          ]
        },
      
        Other: {
          primary: [
            'news', 'breaking', 'update', 'report', 'announcement', 'event',
            'story', 'article', 'interview', 'analysis', 'opinion',
            'comment', 'discussion', 'trend', 'viral', 'popular',
            'اخبار', 'خبر', 'گزارش', 'اعلام', 'رویداد', 'داستان',
            'مقاله', 'مصاحبه', 'تحلیل', 'نظر', 'بحث', 'ترند'
          ],
          secondary: [
            'journalism', 'editorial', 'correspondent', 'headline',
            'press release', 'media coverage', 'public interest',
            'current affairs', 'breaking news'
          ]
        }
    }), []);
    // Add to pages with scrolling issues
const ScrollPerformanceMonitor = () => {
  useEffect(() => {
    let scrollCount = 0;
    let lastScrollTime = performance.now();
    
    const handleScroll = () => {
      scrollCount++;
      const currentTime = performance.now();
      
      // Measure scroll frequency
      if (currentTime - lastScrollTime > 1000) {
        console.log(`📊 Scroll events per second: ${scrollCount}`);
        
        if (scrollCount > 100) {
          console.warn('🔴 Too many scroll events - consider throttling');
        }
        
        scrollCount = 0;
        lastScrollTime = currentTime;
      }
    };
    
    // Throttled scroll listener
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);
  
  return null;
};


    // Your existing functions remain the same...
    const analyzeTextForCategory = useCallback((text) => {
        const textLower = text.toLowerCase();
        const words = textLower.split(/\s+/);
        const categoryScores = {};

        Object.entries(categoryKeywords).forEach(([categoryName, { primary, secondary }]) => {
            let score = 0;
            
            primary.forEach(keyword => {
                if (textLower.includes(keyword.toLowerCase())) {
                    score += 10;
                }
            });
            
            secondary.forEach(keyword => {
                const keywordWords = keyword.toLowerCase().split(/\s+/);
                
                if (textLower.includes(keyword.toLowerCase())) {
                    score += 3;
                } else if (keywordWords.some(word => words.includes(word))) {
                    score += 1;
                }
            });
            
            const relatedTerms = [...primary, ...secondary].filter(term => 
                textLower.includes(term.toLowerCase())
            );
            if (relatedTerms.length > 2) {
                score += 5;
            }
            
            categoryScores[categoryName] = score;
        });
        
        return categoryScores;
    }, [categoryKeywords]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (text.length > 30) {
                const categoryScores = analyzeTextForCategory(text);
                
                const suggestedCategories = Object.entries(categoryScores)
                    .filter(([cat, score]) => score > 3 && !selectedCategories.includes(cat))
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 3)
                    .map(([cat, score]) => ({ category: cat, score }));
                
                setSuggestions(suggestedCategories);
                setShowSuggestions(suggestedCategories.length > 0);
            } else {
                setShowSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [text, selectedCategories, analyzeTextForCategory]);

    // ✅ UPDATED: Handle category button toggle
    const handleCategoryToggle = (category) => {
        setSelectedCategories(prev => {
            if (prev.includes(category)) {
                return prev.filter(c => c !== category);
            } else {
                if (prev.length >= 3) {
                    alert('You can select maximum 3 categories');
                    return prev;
                }
                return [...prev, category];
            }
        });
    };

    const handleSuggestionSelect = (category) => {
        if (selectedCategories.length >= 3) {
            alert('You can select maximum 3 categories');
            return;
        }
        setSelectedCategories(prev => [...prev, category]);
        setShowSuggestions(false);
    };

    // All your existing useEffect hooks remain unchanged...
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/stats/submit`);
                if (response.ok) {
                    const data = await response.json();
                    setStats({
                        predictionsThisWeek: data.predictionsThisWeek,
                        newUsersThisWeek: data.newUsersThisWeek
                    });
                }
            } catch (error) {
                console.error('Error fetching submit stats:', error);
            }
        };

        fetchStats();
    }, []);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry, index) => {
                    if (entry.isIntersecting) {
                        setTimeout(() => {
                            entry.target.style.opacity = '1';
                            entry.target.style.transform = 'translateY(0)';
                        }, index * 200);
                    }
                });
            },
            { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
        );

        const elements = document.querySelectorAll('.submit-header, .submit-form, .prediction-examples, .form-section');
        elements.forEach((el, index) => {
            el.style.opacity = '0';
            el.style.transform = 'translateY(30px)';
            el.style.transition = `opacity 0.8s ease ${index * 0.1}s, transform 0.8s ease ${index * 0.1}s`;
            observerRef.current?.observe(el);
        });

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);
    // ✨ Track form completion progress
useEffect(() => {
    const hasText = text.trim().length >= 10;
    const hasCategory = selectedCategories.length > 0;
    const isReadyToSubmit = hasText && isLoggedIn;

    setFormProgress({
        hasText,
        hasCategory,
        isReadyToSubmit
    });
}, [text, selectedCategories, isLoggedIn]);

    useEffect(() => {
        let backgroundElementsCreated = false;
        
        const createBackgroundElements = () => {
            if (backgroundElementsCreated) return;

            const existingElements = document.querySelectorAll('[data-submit-bg="true"]');
            existingElements.forEach(el => {
                try {
                    if (el && el.parentNode) {
                        el.parentNode.removeChild(el);
                    }
                } catch (error) {
                    console.log('Background cleanup error (safe to ignore):', error);
                }
            });

            try {
                const gridElement = document.createElement('div');
                gridElement.className = 'background-grid';
                gridElement.setAttribute('data-submit-bg', 'true');
                document.body.appendChild(gridElement);

                const glow1 = document.createElement('div');
                glow1.className = 'ambient-glow';
                glow1.setAttribute('data-submit-bg', 'true');
                document.body.appendChild(glow1);

                const glow2 = document.createElement('div');
                glow2.className = 'ambient-glow-2';
                glow2.setAttribute('data-submit-bg', 'true');
                document.body.appendChild(glow2);

                backgroundElementsCreated = true;
            } catch (error) {
                console.error('Error creating background elements:', error);
            }
        };

        createBackgroundElements();

        return () => {
            setTimeout(() => {
                try {
                    const elements = document.querySelectorAll('[data-submit-bg="true"]');
                    elements.forEach(el => {
                        try {
                            if (el && el.parentNode && el.parentNode.contains(el)) {
                                el.parentNode.removeChild(el);
                            }
                        } catch (error) {
                            console.log('Background cleanup error (safe to ignore):', error);
                        }
                    });
                } catch (error) {
                    console.log('Background cleanup error (safe to ignore):', error);
                }
            }, 0);
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('🚀 Starting submission process...');
        
        // Clear any existing error message
        setErrorMessage('');
        
        if (!isLoggedIn) {
            setErrorMessage('Please log in to submit predictions');
            navigate('/login');
            return;
        }
    
        if (!token) {
            setErrorMessage('Authentication token missing. Please log in again.');
            navigate('/login');
            return;
        }
    
        if (!text.trim()) {
            setErrorMessage('Prediction text is required');
            textareaRef.current?.focus();
            return;
        }
        
        if (text.trim().length < 10) {
            setErrorMessage('Prediction must be at least 10 characters long');
            textareaRef.current?.focus();
            return;
        }
        
        if (text.length > 500) {
            setErrorMessage('Prediction text exceeds 500 characters');
            textareaRef.current?.focus();
            return;
        }
        
// ✅ Categories are now optional - no validation needed
// Users can submit predictions without selecting any category

    
// ✅ Only validate if categories are actually selected
if (selectedCategories.length > 0) {
    const validCategories = [
        'Politics', 'Economy', 'Environment', 'Technology', 
        'Sports', 'Entertainment', 'Healthcare', 'Society', 
        'Trendphile', 'Other'
    ];
    
    const invalidCategories = selectedCategories.filter(cat => !validCategories.includes(cat));
    if (invalidCategories.length > 0) {
        setErrorMessage(`Invalid categories: ${invalidCategories.join(', ')}`);
        return;
    }
}

        
        setIsSubmitting(true);
        
const payload = {
    prediction_text: text.trim(),
    categories: selectedCategories.length > 0 ? selectedCategories.join(',') : null, // ✅ Allow null
    visibility: Boolean(visibility)
};

        
        console.log('📤 Payload being sent:', payload);
        
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/predictions`,
                payload,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 30000
                }
            );
            
            console.log('✅ Submission successful:', response.data);
            
            // 🤖 Check if prediction was flagged for AI content
            if (response.data.prediction?.flagged && response.data.details?.warning) {
                // Show AI warning message instead of generic success
                setErrorMessage(
                    `⚠️ ${response.data.message}\n\n${response.data.details.warning}\n\n` +
                    `💡 Suggestions:\n${response.data.details.suggestions.join('\n')}`
                );
                
                // Still clear form after delay
                setTimeout(() => {
                    setText('');
                    setSelectedCategories([]);
                    setVisibility(true);
                    setErrorMessage('');
                    window.location.reload();
                }, 5000); // Longer delay to read the message
                
            } else {
                // ✅ Store prediction data
                const predictionData = {
                    id: response.data.prediction?.id,
                    prediction_text: text,
                    category: selectedCategories.length > 0 ? selectedCategories[0] : 'Other',
                    categories: selectedCategories.join(','),
                    reliability_score: response.data.prediction?.reliability_score,
                    timestamp: new Date().toISOString()
                };
                
                console.log('📦 Storing prediction data:', predictionData);
                setSubmittedPrediction(predictionData);
                
                // ✅ Show success animation
                setShowSuccess(true);
                
                // ✅ After 2 seconds, hide success and show share modal
                setTimeout(() => {
                    setShowSuccess(false);
                    setShowShareModal(true);
                }, 2000);
            }
            
            
        } catch (err) {
            console.error('❌ Submission error:', {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status,
                payload: payload
            });
            
            let errorMessage = 'Unknown error occurred';
            
            if (err.response) {
                const status = err.response.status;
                const data = err.response.data;
        
                
                if (status === 400) {
                    // 🎯 Handle specific error types from backend
                    if (data && data.error === 'duplicate') {
                        // Duplicate or similar prediction detected
                        errorMessage = data.message || '⚠️ Duplicate Prediction Detected';
                        if (data.details?.suggestions) {
                            errorMessage += '\n\n' + data.details.suggestions.join('\n');
                        }
                    } else if (data && data.error === 'contradiction') {
                        // Contradictory prediction detected
                        errorMessage = data.message || '⚠️ Contradictory Prediction Detected';
                        if (data.details?.suggestions) {
                            errorMessage += '\n\n' + data.details.suggestions.join('\n');
                        }
                    } else if (data && data.message && (data.message.includes('Duplicate') || data.message.includes('Similar'))) {
                        // Fallback for legacy duplicate detection
                        errorMessage = data.message;
                        if (data.details?.suggestions) {
                            errorMessage += '\n\n' + data.details.suggestions.join('\n');
                        }
                    } else if (data && data.message) {
                        errorMessage = data.message;
                    } else {
                        errorMessage = 'Bad Request: Please check your input data';
                    }
                } else if (status === 401) {
                    errorMessage = 'Authentication failed. Please log in again.';
                    localStorage.removeItem('token');
                    navigate('/login');
                    return;
                } else if (status === 403) {
                    console.log('🔍 403 Error - Full data object:', data);
                    
                    // 🎯 Handle non-AI fraud blocking (spam, manipulation, etc.)
                    if (data?.message) {
                        errorMessage = data.message;
                        
                        // Add recommendations if available
                        if (data.details?.recommendations && Array.isArray(data.details.recommendations)) {
                            const recList = data.details.recommendations
                                .map(r => `• ${r}`)
                                .join('\n');
                            errorMessage = `${data.message}\n\n${recList}`;
                        }
                    } else {
                        errorMessage = 'Access denied. Please check your permissions.';
                    }
                
                } else if (status === 409) {
                    errorMessage = 'This prediction already exists! Try sharing a different vision of the future.';
                } else if (status === 422) {
                    errorMessage = 'Invalid data format. Please check your input.';
                } else if (status >= 500) {
                    errorMessage = 'Server error. Please try again later.';
                } else {
                    errorMessage = `Server error (${status}): ${data?.message || 'Please try again'}`;
                }
            } else if (err.request) {
                console.error('🌐 Network error - no response received');
                errorMessage = 'Network error: Unable to connect to server. Please check your internet connection.';
            } else {
                console.error('⚙️ Request setup error:', err.message);
                errorMessage = `Request error: ${err.message}`;
            }
            
            setErrorMessage(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };
    

    const characterCount = text.length;
    const isNearLimit = characterCount > 400;
    const isAtLimit = characterCount >= 500;

    const getConfidenceIcon = (score) => {
        if (score > 10) return <Zap className="confidence-icon high" />;
        if (score > 5) return <Award className="confidence-icon medium" />;
        return <Brain className="confidence-icon low" />;
    };

    const predictionExamples = [
        {
            icon: <Bot className="example-icon-svg" />,
            title: "AI & Technology",
            example: "AI will revolutionize medical diagnosis by 2026..."
        },
        {
            icon: <Earth className="example-icon-svg" />,
            title: "Climate & Environment", 
            example: "Renewable energy will reach 80% global adoption..."
        },
        {
            icon: <DollarSign className="example-icon-svg" />,
            title: "Economics & Markets",
            example: "Cryptocurrency will become mainstream currency..."
        }
    ];

    if (showSuccess) {
        console.log('✨ Rendering success animation');
        // ✅ NO auto-reload - modal will handle cleanup
        return (
            <div className="submit-container">
                <div className="success-overlay">
                    <div className="success-content">
                        {/* Golden Crescent Moon Animation */}
                        <div className="success-moon-wrapper">
                            <div className="success-moon-crescent">
                                <Moon size={80} strokeWidth={2} />
                            </div>
                            <div className="success-moon-glow"></div>
                            {/* Orbiting particles */}
                            <div className="success-moon-particles">
                                <span className="success-particle"></span>
                                <span className="success-particle"></span>
                                <span className="success-particle"></span>
                            </div>
                        </div>
                        <h2 className="success-title">Prediction Launched!</h2>
                        <p className="success-subtitle">Your vision of the future has been shared with the world</p>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="submit-container">
            {!isLoggedIn && showLoginBanner && (
                <div className="elegant-login-banner">
                    <div className="banner-content">
                        <div className="banner-icon-group">
                            <Lock className="banner-lock-icon" />
                            <User className="banner-user-icon" />
                        </div>
                        <div className="banner-text">
                            <h3>Ready to share your vision?</h3>
                            <p>Join thousands of visionaries and unlock prediction submission</p>
                        </div>
                        <div className="banner-actions">
                            <button onClick={() => navigate('/login')} className="elegant-login-btn">
                                Sign In
                            </button>
                            <button 
                                onClick={() => setShowLoginBanner(false)} 
                                className="banner-close-btn"
                                aria-label="Close banner"
                            >
                                <X className="close-icon" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
    
            <div className="container">
                <section className="submit-header">
                    <h1 className="submit-title">
                        <span className="title-main typing-animation">Share Your</span>
                        <span className="title-accent typing-animation-delay">Prediction</span>
                    </h1>
                    
                    <p className="submit-subtitle">
                        Join thousands of visionaries shaping tomorrow's understanding. 
                        Your insights could reveal the next breakthrough.
                    </p>
                </section>
    
                <div className="submit-grid">
                    <div className="form-container">
                        <form className="submit-form glass-card" onSubmit={handleSubmit}>
                            {/* 🚨 ELEGANT ERROR MESSAGE - Shows appropriate error type */}
                            {errorMessage && (
    <div className={`elegant-error-message ${
        errorMessage.includes('AI content') || errorMessage.includes('🤖') ? 'ai-detected-warning' : 
        errorMessage.includes('Duplicate') || errorMessage.includes('Similar') ? 'duplicate-warning' :
        errorMessage.includes('Contradict') ? 'contradiction-warning' : ''
    }`}>

                                    <div className="error-icon-wrapper">
                                        <AlertTriangle className="error-icon" />
                                    </div>
                                    <div className="error-content">
                                        <h4 className="error-title">
                                            {errorMessage.includes('Duplicate') || errorMessage.includes('Similar') || errorMessage.includes('already exists') 
                                                ? '⚠️ Duplicate Prediction Detected' 
                                                : errorMessage.includes('Contradict') 
                                                    ? '⚠️ Contradictory Prediction Detected'
                                                    : errorMessage.includes('AI content') || errorMessage.includes('🤖')
                                                        ? '🤖 AI Content Detected'
                                                        : '❌ Submission Error'}
                                        </h4>
                                        <p className="error-description">{errorMessage}</p>
                                    </div>
                                    <button 
                                        onClick={() => setErrorMessage('')} 
                                        className="error-dismiss-btn"
                                        aria-label="Dismiss error"
                                    >
                                        <X className="dismiss-icon" />
                                    </button>
                                </div>
                            )}
    
                            <div className="form-section">
                                <div className="textarea-container">
                                    <textarea
                                        ref={textareaRef}
                                        className={`form-textarea enhanced-textarea ${isNearLimit ? 'near-limit' : ''} ${isAtLimit ? 'at-limit' : ''}`}
                                        maxLength={500}
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder="What transformative change do you foresee? Share your vision of the future... #technology #innovation #future"
                                        required
                                        disabled={!isLoggedIn}
                                    />
                                    <div className={`character-counter animated-counter ${isNearLimit ? 'warning' : ''} ${isAtLimit ? 'danger' : ''}`}>
                                        <span className="counter-number">{characterCount}</span>
                                        <span className="counter-divider">/</span>
                                        <span className="counter-max">500</span>
                                    </div>
                                </div>
    
                                {showSuggestions && (
                                    <div className="category-suggestions">
                                        <div className="suggestions-header">
                                            <Brain className="suggestions-icon" />
                                            <p className="suggestions-label">Smart category suggestions based on your text:</p>
                                        </div>
                                        <div className="suggestions-list">
                                            {suggestions.map((suggestion) => (
                                                <button
                                                    key={suggestion.category}
                                                    type="button"
                                                    className="suggestion-chip"
                                                    onClick={() => handleSuggestionSelect(suggestion.category)}
                                                    disabled={selectedCategories.length >= 3}
                                                >
                                                    <span className="chip-text">{suggestion.category}</span>
                                                    <span className="chip-confidence">
                                                        {getConfidenceIcon(suggestion.score)}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
    
{/* ✨ INLINE PROGRESS TRACKER - MOVED ABOVE CATEGORIES */}
<div className="form-completion-tracker">
    <div className={`completion-step ${formProgress.hasText ? 'complete' : text.length > 0 ? 'in-progress' : ''}`}>
        <div className="step-indicator">
            {formProgress.hasText ? (
                <div className="step-check">✓</div>
            ) : (
                <div className="step-number">1</div>
            )}
        </div>
        <span className="step-label">Write prediction</span>
    </div>

    <div className="progress-connector"></div>

    <div className={`completion-step ${formProgress.hasCategory ? 'complete' : selectedCategories.length > 0 ? 'in-progress' : ''}`}>
        <div className="step-indicator">
            {formProgress.hasCategory ? (
                <div className="step-check">✓</div>
            ) : (
                <div className="step-number">2</div>
            )}
        </div>
        <span className="step-label">Select category (optional)</span>
    </div>

    <div className="progress-connector"></div>

    <div className={`completion-step ${formProgress.isReadyToSubmit ? 'active ready-to-launch' : ''}`}>
        <div className="step-indicator">
            {formProgress.isReadyToSubmit ? (
                <Rocket className="step-icon-rocket" size={16} />
            ) : (
                <div className="step-number">3</div>
            )}
        </div>
        <span className="step-label">
            {formProgress.isReadyToSubmit ? 'Ready to Launch!' : 'Launch prediction (scroll down to publish)'}
        </span>
    </div>
</div>

                            <div className="form-section">
                                <div className="category-button-grid two-row-grid">
                                    {availableCategories.map(category => {
                                        const IconComponent = categoryIcons[category];
                                        const displayLabel = categoryLabels[category];
                                        
                                        return (
                                            <button
                                                key={category}
                                                type="button"
                                                className={`category-button icon-category-btn ${selectedCategories.includes(category) ? 'selected' : ''} ${!isLoggedIn ? 'disabled' : ''}`}
                                                onClick={() => handleCategoryToggle(category)}
                                                disabled={!isLoggedIn || (!selectedCategories.includes(category) && selectedCategories.length >= 3)}
                                            >
                                                <IconComponent className="category-btn-icon" size={18} />
                                                <span className="category-btn-label">{displayLabel}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                
                                {selectedCategories.length > 0 && (
                                    <div className="selected-categories-summary">
                                        <span className="summary-label">Selected:</span>
                                        <span className="summary-categories">{selectedCategories.join(', ')}</span>
                                        <span className="summary-count">({selectedCategories.length}/3)</span>
                                    </div>
                                )}
                            </div>
    
                            <div className="form-section">
                                <div className="visibility-toggle enhanced-toggle">
                                    <div className="toggle-options">
                                        <label className={`toggle-option ${visibility ? 'active' : ''} ${!isLoggedIn ? 'disabled' : ''}`}>
                                            <input
                                                type="radio"
                                                checked={visibility}
                                                onChange={() => setVisibility(true)}
                                                disabled={!isLoggedIn}
                                            />
                                            <span className="option-content">
                                                <Globe className="option-icon-svg" />
                                                <span>Public</span>
                                            </span>
                                        </label>
                                        
                                        <label className={`toggle-option ${!visibility ? 'active' : ''} ${!isLoggedIn ? 'disabled' : ''}`}>
                                            <input
                                                type="radio"
                                                checked={!visibility}
                                                onChange={() => setVisibility(false)}
                                                disabled={!isLoggedIn}
                                            />
                                            <span className="option-content">
                                                <Lock className="option-icon-svg" />
                                                <span>Private</span>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
    
<button 
    className={`submit-button enhanced-submit ${isSubmitting ? 'submitting' : ''} ${!isLoggedIn ? 'login-required' : ''} ${formProgress.isReadyToSubmit ? 'ready-to-launch pulsing' : ''} sticky-mobile`} 
    type="submit"
    disabled={isSubmitting || !text.trim() || !isLoggedIn}
>

                                {!isLoggedIn ? (
                                    <>
                                        <Shield className="button-icon-svg" />
                                        <span>Login Required to Submit</span>
                                    </>
                                ) : isSubmitting ? (
                                    <>
                                        <Loader className="loading-spinner-svg" />
                                        <span>Publishing Prediction...</span>
                                        <div className="loading-skeleton"></div>
                                    </>
                                ) : (
                                    <>
                                        <Rocket className="button-icon-svg" />
                                        <span>(Launch Prediction)</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
    
                    {/* Rest of your sidebar content remains the same */}
                    <div className="prediction-examples glass-card">
                        <h3 className="examples-title">Need Inspiration?</h3>
                        <p className="examples-subtitle">
                            Here are some categories to spark your foresight
                        </p>
                        
                        <div className="examples-list">
                            {predictionExamples.map((example, index) => (
                                <div key={index} className="example-card simple-card">
                                    <div className="example-icon">{example.icon}</div>
                                    <div className="example-content">
                                        <h4 className="example-title">{example.title}</h4>
                                        <p className="example-text">{example.example}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
    
                        <div className="community-stats enhanced-stats">
                            <div className="stat-item">
                                <span className="stat-number animated-number">{stats.predictionsThisWeek.toLocaleString()}</span>
                                <span className="stat-label">Predictions this week</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-number animated-number">{stats.newUsersThisWeek}</span>
                                <span className="stat-label">New Users This Week</span>
                            </div>
                        </div>
    
                        {!isLoggedIn && (
                            <div className="guest-benefits enhanced-benefits">
                                <h4>Why Create an Account?</h4>
                                <ul>
                                    <li><Target className="benefit-icon" /> Track your prediction quality</li>
                                    <li><Trophy className="benefit-icon" /> Share your vision of tomorrow</li>
                                    <li><BarChart className="benefit-icon" /> Access detailed analytics</li>
                                    <li><Star className="benefit-icon" /> Become a future visionary</li>
                                </ul>
                                <button onClick={() => navigate('/signup')} className="signup-btn enhanced-signup">
                                    <UserPlus className="signup-icon" />
                                    Get Started Free
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ✅ Social Share Modal */}
            {console.log('🎭 Modal Render Check:', { 
                showShareModal, 
                hasPrediction: !!submittedPrediction,
                predictionId: submittedPrediction?.id,
                hasToken: !!token 
            })}
<ShareSuccessModal
    isOpen={showShareModal}
    prediction={submittedPrediction}
    userToken={token}
    onClose={() => {
        console.log('🚪 Modal close triggered');
        setShowShareModal(false);
        setSubmittedPrediction(null);
        // Don't navigate immediately - just close
        // User can manually go to home if they want
    }}
/>
        </div>
    );
}
  

export default Submit;
